import classNames from "classnames/bind";
import { InclusivelyHidden } from "../InclusivelyHidden/InclusivelyHidden";
import Link from "next/link";
import styles from "./ProjectGrid.module.css";
import { useReproducibleRandomNumberGenerator } from "../../useReproducibleRandomNumberGenerator";
import { imageBuilder } from "../../sanity/lib/image";
import { useEffect, useRef, useState } from "react";

const VIEWPORT_WIDTHS = [320, 360, 375, 414, 768, 1024, 1280, 1366, 1440, 1728, 1920];
const ALIGNMENTS = ["start", "center", "end"];
const CROPS = [
  /* 16:9 */ 16 / 9,
  /* 4:3 */ 4 / 3,
  /* 1:1 */ 1 / 1,
  /* 3:4 */ 3 / 4,
  /* 9:16 */ 9 / 16,
];
const SIZES = [1.1, 1.0, 0.9];

/**
 * This aspect-ratio is the one which will appear as "full-width".
 */
const BASE_ASPECT_RATIO = 16 / 9;

const cx = classNames.bind(styles);

type Item = { title: string; url: string; thumbnailUrl: string; hotspot: { x: number; y: number } };

export const ProjectGrid = ({ items }: { items: Item[] }) => {
  const rng = useReproducibleRandomNumberGenerator();

  const getRandomAlignment = () => ALIGNMENTS[Math.floor(rng() * ALIGNMENTS.length)];
  const getRandomCrop = () => CROPS[Math.floor(rng() * CROPS.length)];
  const getRandomSize = () => SIZES[Math.floor(rng() * SIZES.length)];

  const intersectionRef = useRef<HTMLDivElement | null>(null);

  /**
   * Here's a mechanism for lazy-loading projects as the bottom is scrolled into
   * view.
   */
  const [count, setCount] = useState<number>(6);
  useEffect(() => {
    if (count <= items.length) {
      const intersectionObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            // Render a new row of projects
            setCount((current) => current + 2);
          }
        }
      });

      if (intersectionRef.current) {
        intersectionObserver.observe(intersectionRef.current);
      }

      return () => intersectionObserver.disconnect();
    }
  }, [
    items.length,
    /**
     * Deliberately using `count` as a dependency here, because we might load a
     * row that does not bump the `intersectionRef` element out of the viewport.
     * Re-initialising the IntersectionObserver ensures that we keep loading
     * rows as needed.
     */
    count,
  ]);

  return (
    <ul className={cx("container")}>
      {items.slice(0, count).map((item, index) => {
        const aspectRatio = getRandomCrop();

        return (
          <Cell
            key={index}
            item={item}
            size={Math.min(1, Math.sqrt(aspectRatio / BASE_ASPECT_RATIO) * getRandomSize())}
            placement={`${getRandomAlignment()} ${getRandomAlignment()}`}
            aspectRatio={aspectRatio}
            // Eagerly load the above-the-fold images, lazily load the rest.
            loading={index < 2 ? "eager" : "lazy"}
          />
        );
      })}
      <div
        // Element for detecting when we've scrolled to bottom of the page
        ref={intersectionRef}
      />
    </ul>
  );
};

export const Cell = ({
  item,
  size,
  placement,
  aspectRatio,
  loading,
}: {
  item: Item;
  size: number;
  placement: string;
  aspectRatio: number;
  loading: "eager" | "lazy";
}) => {
  const [tooltipMode, setTooltipMode] = useState<
    /* Follow the cursor around */
    | "cursor"
    /* Hang below the image (primarily for keyboard users) */
    | "below"
  >("below");

  const tooltipRef = useRef<HTMLDivElement | null>(null);

  return (
    <li
      className={cx("cell")}
      style={{
        width: `calc(${size} * 100%)`,
        placeSelf: placement,
      }}
      onFocus={() => setTooltipMode("below")}
      onMouseEnter={() => setTooltipMode("cursor")}
      onMouseMove={(event) => {
        const cellElement = event.currentTarget;
        const tooltipElement = tooltipRef.current;

        requestAnimationFrame(() => {
          const cellRect = cellElement.getBoundingClientRect();

          const left = event.clientX - cellRect.left;
          const top = event.clientY - cellRect.top;
          tooltipElement?.style.setProperty("transform", `translate3d(${left}px, ${top}px, 0)`);
        });
      }}
    >
      <Link className={cx("link")} href={item.url} onFocus={() => setTooltipMode("below")}>
        <InclusivelyHidden as="span">{item.title}</InclusivelyHidden>
      </Link>
      <img
        className={cx("image")}
        style={{ aspectRatio: aspectRatio }} // Reduces jank because the browser knows how much real-estate to allocate to the image before it's loaded.
        srcSet={VIEWPORT_WIDTHS.map((width) => {
          const src = imageBuilder
            .image(item.thumbnailUrl)
            .format("webp")
            .width(width)
            .height(Math.ceil(width / aspectRatio))
            .fit("crop")
            .crop("focalpoint")
            .focalPoint(item.hotspot.x, item.hotspot.y)
            .quality(90);

          return `${src} ${width}w`;
        }).join(",\n")}
        src={item.thumbnailUrl}
        sizes={`(min-width: 646px) min(${Math.round(size * 864)}px, ${Math.round(size * 50)}vw), ${Math.round(size * 100)}vw`}
        loading={loading}
        alt=""
      />
      <div
        /**
         * Deliberately not marking this up as role=tooltip, because the content
         * is redundant - it's a duplicate of the InclusivelyHidden link's
         * content above. This tooltip provides purely aesthetic functionality
         * for sighted users only.
         */
        aria-hidden="true"
        className={cx("tooltip")}
        data-mode={tooltipMode}
        ref={tooltipRef}
      >
        {item.title}
      </div>
    </li>
  );
};
